import { useState, useCallback, useMemo } from "react";
import {
  Table,
  Typography,
  Row,
  Col,
  Modal,
  Input,
  Form,
  Popconfirm,
  Button,
  Popover,
} from "antd";
import PropTypes from "prop-types";
import { DeleteOutlined } from "@ant-design/icons";

import { useAlertStore } from "../../../../store/alert-store";
import "./ResultEditor.css";

function ResultEditor({
  data,
  queueData,
  setQueueData,
  updateEditedData,
  type,
  highlightText,
  highlightMetadata,
  confidenceMetadata,
  setHighlightData,
}) {
  const [editData, setEditData] = useState({
    key: "",
    value: "",
    rowIndex: null,
    path: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatedData, setUpdatedData] = useState(data);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [isAddRowModalOpen, setIsAddRowModalOpen] = useState(false);
  const [currentNewRow, setCurrentNewRow] = useState({});
  const [currentKey, setCurrentKey] = useState();
  const [currentPath, setCurrentPath] = useState();
  const { setAlertDetails } = useAlertStore();
  const [currentSelected, setCurrentSelected] = useState();

  const showModal = () => {
    setIsModalOpen(true);
  };
  const updateData = (newData) => {
    setUpdatedData(newData);
    queueData.result.output = newData;
    setQueueData(queueData);
    updateEditedData();
    setCurrentNewRow({});
    setIsModalOpen(false);
  };
  // Utility function to update nested values
  const updateNestedValue = (obj, path, callback) => {
    let temp = obj;
    for (let i = 0; i < path.length - 1; i++) {
      temp = temp[path[i]];
    }
    callback(temp, path[path.length - 1]);
  };
  const requiredField = (key) =>
    !!queueData?.result?.metadata?.required_fields?.[key];

  const handleOk = () => {
    const newData = { ...updatedData };
    updateNestedValue(newData, editData.path, (temp, lastKey) => {
      temp[lastKey] = editData.value;
    });
    updateData(newData);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsAddRowModalOpen(false);
  };

  function getValueFromPath(data, path) {
    return path?.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
      data
    );
  }
  const editValue = (key, rowIndex, path = []) => {
    const value = getValueFromPath(updatedData, path);
    setEditData({
      key: key,
      value: typeof value === "object" ? JSON.stringify(value) : value,
      rowIndex: rowIndex,
      path: path,
    });
    showModal();
  };

  const handleClick = (key, rowIndex, path = []) => {
    setCurrentSelected(key);
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (type !== "download") {
        editValue(key, rowIndex, path);
      } else {
        setAlertDetails({
          type: "error",
          content: "View only mode",
        });
      }
    } else {
      setClickTimeout(
        setTimeout(() => {
          setHighlightData(
            highlightText(path, highlightMetadata, confidenceMetadata)
          );
          setClickTimeout(null);
        }, 300) // Adjust the delay as needed
      );
    }
  };

  const addRowModal = (key, path) => {
    setCurrentKey(key);
    setCurrentPath(path);
    const newRow = {}; // Adjust as per the structure of your rows
    const newData = { ...updatedData };
    const keys = Object.keys(newData[key][0]);
    keys.forEach((k) => {
      newRow[k] = ""; // Set each key to an empty string
    });
    setCurrentNewRow(newRow);
    setIsAddRowModalOpen(true);
  };

  const addRowSave = () => {
    const key = currentKey;
    const path = currentPath;
    const newData = { ...updatedData };
    addRow(key, path, newData);
    setIsAddRowModalOpen(false);
  };

  const addRow = (key, path, newData) => {
    const newRow = { ...currentNewRow }; // Use spread to avoid direct mutation
    const newPath = [...path, key];

    updateNestedValue(newData, newPath, (temp, lastKey) => {
      if (Array.isArray(temp[lastKey])) {
        temp[lastKey].push(newRow);
      }
    });

    updateData(newData);
  };

  const deleteRow = (rowIndex, key, path) => {
    const newData = { ...updatedData };
    const newPath = [...path, key];

    updateNestedValue(newData, newPath, (temp, lastKey) => {
      if (Array.isArray(temp[lastKey])) {
        temp[lastKey].splice(rowIndex, 1);
      }
    });

    updateData(newData);
  };
  function cleanEmptyValues(data) {
    if (Array.isArray(data)) {
      return data
        .map(cleanEmptyValues) // Clean each item recursively
        .filter(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== "" &&
            !(Array.isArray(item) && item.length === 0) &&
            !(
              typeof item === "object" &&
              item !== null &&
              Object.keys(item).length === 0
            )
        );
    } else if (typeof data === "object" && data !== null) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        const cleanedValue = cleanEmptyValues(value);
        if (
          cleanedValue !== null &&
          cleanedValue !== undefined &&
          cleanedValue !== "" &&
          !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
          !(
            typeof cleanedValue === "object" &&
            cleanedValue !== null &&
            Object.keys(cleanedValue).length === 0
          )
        ) {
          acc[key] = cleanedValue;
        }
        return acc;
      }, {});
    }
    return data;
  }

  const getColorByConfidence = (key, path) => {
    const confidenceList = queueData?.result?.metadata?.confidence_data || [];
    let validConfidenceEntries = getValueFromPath(confidenceList, path);
    validConfidenceEntries = cleanEmptyValues(validConfidenceEntries);
    if (!validConfidenceEntries || validConfidenceEntries.length <= 0) {
      return [];
    }
    const confidence =
      validConfidenceEntries.length !== 0
        ? parseFloat(validConfidenceEntries?.[key]?.[0]?.confidence)
        : 100;
    const normalizedConfidence = Math.max(
      0,
      Math.min(1, parseFloat(confidence) / 100)
    );
    // Map the confidence score to a color scale from light red to dark red (lower to higher)
    const redIntensity = Math.floor((1 - normalizedConfidence) * 255); // Lower confidence = more red
    let toolTipContent = null;
    if (validConfidenceEntries?.[key]?.length > 0) {
      toolTipContent = (
        <div className="mrq-confidence-popover">
          {validConfidenceEntries?.[key].map((item, index) => (
            <>
              <Row key={index}>
                <Col span={12}>Score</Col>
                <Col span={12}>{item[0].confidence}</Col>
              </Row>
              <Row key={index + "text"}>
                <Col span={12}>Text</Col>
                <Col span={12}>{item[0].text}</Col>
              </Row>
            </>
          ))}
        </div>
      );
    }
    return [`rgb(${redIntensity}, 0, 0)`, toolTipContent];
  };
  const drawEditableResult = (obj, path = []) => {
    const traverse = (obj, path) => {
      return Object.keys(obj).map((key) => {
        const value = obj[key];

        if (Array.isArray(value)) {
          return renderArray(value, key, path);
        } else if (typeof value === "object" && value !== null) {
          return renderObject(value, key, path);
        } else if (typeof value === "string") {
          return renderString(value, key, path);
        } else if (typeof value === "number") {
          return renderString(value, key, path);
        } else {
          if (value === undefined) {
            return renderString("-", key, path);
          }
          return renderString(JSON.stringify(value), key, path);
        }
      });
    };

    const renderArray = (array, key, path) => {
      const firstItem = array[0];

      if (typeof firstItem === "object" && firstItem !== null) {
        return renderObjectArray(array, key, path);
      }
      array = array?.map((item) => (item === null ? "---" : String(item)));
      return renderStringArray(array, key, path);
    };

    const renderStringArray = (array, key, path) => {
      const [, confidenceMessage] = getColorByConfidence(key);
      return (
        <div key={key}>
          <Col span={24} className="mrq-table-cell mrq-table-key">
            <strong>
              {key}: {requiredField(key) && <span className="required">*</span>}
            </strong>
          </Col>
          {array.map((item, index) => (
            <Row
              key={index}
              className="individual-rows mrq-table-row mrq-string-row"
              onClick={() => {
                const currentPath = [...path, key, index];
                handleClick(index, null, currentPath);
              }}
            >
              <Col
                span={24}
                className={`mrq-table-cell mrq-table-value ${
                  confidenceMessage ? "mrq-low-confidence" : ""
                }`}
              >
                <Popover
                  placement="top"
                  title={
                    confidenceMessage && (
                      <Typography className="mrq-confidence-popover">
                        Low Confidence
                      </Typography>
                    )
                  }
                  content={confidenceMessage}
                >
                  <Typography.Text
                    className={`formLabel mrq-editable-cell ${
                      currentSelected === key ? "mrq-current-highlight" : ""
                    }`}
                  >
                    {item}
                  </Typography.Text>
                </Popover>
              </Col>
            </Row>
          ))}
        </div>
      );
    };

    const renderObjectArray = (array, key, path) => {
      const [, confidenceMessage] = getColorByConfidence(key);
      const handleCellClick = useCallback(
        (colKey, rowIndex) => {
          const currentPath = [...path, key, rowIndex, colKey];
          handleClick(colKey, rowIndex, currentPath);
        },
        [path, key]
      );

      const handleDeleteRow = useCallback(
        (rowIndex) => {
          deleteRow(rowIndex, key, path);
        },
        [key, path]
      );

      const columns = useMemo(
        () => [
          ...Object.keys(array[0]).map((colKey) => ({
            title: colKey,
            dataIndex: colKey,
            key: colKey,
            render: (text, _, rowIndex) => (
              <Typography.Text
                className="editable-cell mrq-editable-cell"
                onClick={() => handleCellClick(colKey, rowIndex)}
              >
                <span>
                  {typeof text === "object"
                    ? JSON.stringify(text)
                    : text
                    ? text
                    : "--"}
                </span>
              </Typography.Text>
            ),
          })),
          {
            title: "Actions",
            key: "actions",
            render: (_, __, rowIndex) => (
              <Popconfirm
                title="Delete the row"
                description="Are you sure to delete this row?"
                onConfirm={() => handleDeleteRow(rowIndex)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined />
              </Popconfirm>
            ),
          },
        ],
        [array, handleCellClick, handleDeleteRow]
      );
      return (
        <div key={key}>
          <Popover
            placement="top"
            title={
              confidenceMessage && (
                <Typography className="mrq-confidence-popover">
                  Low Confidence - Table
                </Typography>
              )
            }
            content={confidenceMessage}
          >
            <Typography.Text
              className={`editable-cell mrq-editable-cell ${
                confidenceMessage ? "mrq-low-confidence" : ""
              }`}
            >
              <strong>
                {key}:
                {requiredField(key) && <span className="required">*</span>}
              </strong>
            </Typography.Text>
          </Popover>
          <Button
            className="mrq-add-row"
            onClick={() => addRowModal(key, path)}
            type="primary"
          >
            Add a row
          </Button>

          <Table
            rowClassName={() => "editable-row"}
            bordered
            dataSource={array.map((item, index) => ({ ...item, key: index }))}
            columns={columns}
            pagination={false}
          />
        </div>
      );
    };

    const renderObject = (value, key, path) => (
      <div key={key} className="individual-divs">
        <Typography className="formLabel">
          <strong>
            {key}:{requiredField(key) && <span className="required">*</span>}
          </strong>
        </Typography>
        {traverse(value, [...path, key])}
      </div>
    );

    const renderString = (value, key, path) => {
      const [, confidenceMessage] = getColorByConfidence(key, path);
      const currentPath = [...path, key];
      const handleRowClick = useCallback(() => {
        handleClick(key, null, currentPath);
      }, [key, currentPath]);

      return (
        <Popover
          placement="top"
          title={
            confidenceMessage && (
              <Typography className="mrq-confidence-popover">
                Low Confidence
              </Typography>
            )
          }
          content={confidenceMessage}
        >
          <Row
            key={key}
            className="mrq-individual-rows mrq-table-row"
            onClick={handleRowClick}
          >
            <Col span={12} className="mrq-table-cell mrq-table-key">
              <strong>
                {key}
                {requiredField(key) && <span className="required">*</span>}
              </strong>
            </Col>
            <Col
              span={12}
              className={`mrq-table-cell mrq-table-value ${
                confidenceMessage ? "mrq-low-confidence" : ""
              }`}
            >
              <Typography.Text
                className={`formLabel mrq-editable-cell ${
                  currentSelected === key ? "mrq-current-highlight" : ""
                }`}
              >
                {value}
              </Typography.Text>
            </Col>
          </Row>
        </Popover>
      );
    };
    return <div>{traverse(obj, path)}</div>;
  };

  return (
    <>
      <div
        style={{
          overflow: "scroll",
          padding: "20px",
        }}
      >
        {drawEditableResult(updatedData)}
      </div>
      <Modal
        title="Edit value"
        open={isModalOpen}
        onOk={handleOk}
        okText={"Save"}
        onCancel={handleCancel}
      >
        <div>
          <Form>
            <Form.Item label={String(editData.key)}>
              <Input.TextArea
                value={editData.value}
                onChange={(e) =>
                  setEditData({ ...editData, value: e.target.value })
                }
                rows={4}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
      <Modal
        title="Add Row"
        open={isAddRowModalOpen}
        onOk={addRowSave}
        onCancel={handleCancel}
        okText="Save"
      >
        {Object.keys(currentNewRow).map((key) => {
          return (
            <Row key={key} className="mrq-add-row-form">
              <Col span={12}>{key}</Col>
              <Col span={12}>
                <Input
                  defaultValue={currentNewRow[key]}
                  value={currentNewRow[key]}
                  placeholder={key}
                  onChange={(e) =>
                    setCurrentNewRow({
                      ...currentNewRow,
                      [key]: e.target.value,
                    })
                  }
                />
              </Col>
            </Row>
          );
        })}
      </Modal>
    </>
  );
}

ResultEditor.propTypes = {
  data: PropTypes.any,
  queueData: PropTypes.object,
  setQueueData: PropTypes.func,
  updateEditedData: PropTypes.func,
  type: PropTypes.string,
  highlightText: PropTypes.func,
  highlightMetadata: PropTypes.object,
  confidenceMetadata: PropTypes.object,
  setHighlightData: PropTypes.func,
};

export { ResultEditor };
